/**
 * LanguageSelector — tenant/user-facing language switcher for the authenticated
 * app shell top navigation.
 *
 * Scope (LANGUAGE-SWITCHER-USER-SHELL-A1 + raw-key-fallback repair A1):
 *   - Mounted in `apps/web/src/components/layout/AppHeader.tsx`, the tenant/user
 *     shell header (operations + admin shells). NOT a Platform Admin component.
 *   - Uses the existing i18next instance configured at
 *     `apps/web/src/i18n/config.ts`, which already wires:
 *       * localStorage persistence under key `amuster_locale`,
 *       * the `languageChanged` event handler that calls
 *         `applyLocaleDirection()` to set <html dir="rtl|ltr"> and
 *         <html lang="...">,
 *       * the bundled en/ar locale files as fallback.
 *   - Visible copy resolves through `useTranslationWithCMS` so the
 *     platform-content CMS publication (seeded by
 *     R__platform_i18n_shell_language_selector_copy.sql) is the source of
 *     truth for the selector's own labels.
 *
 * Raw-key-fallback repair (LANGUAGE-SWITCHER-RAW-KEY-FALLBACK-A1):
 *   - PR #619 browser acceptance observed `shell.language.selector.current`
 *     rendered as the dropdown heading on the first dropdown open after
 *     deploy (React Query 5-min CMS cache had been populated before R__ ran
 *     and hadn't refetched yet). The visible raw key violated the no-
 *     compromise UI rule.
 *   - `resolveCmsValue()` below treats nullish, key-equals-value, empty
 *     string, and any `shell.language.` namespace-prefixed return value as
 *     unresolved and returns null. Consumers then either suppress
 *     rendering (heading) or use a locale-native / short-code fallback
 *     (aria-labels). When the CMS value IS resolved, it is rendered
 *     verbatim — the CMS publication remains the source of truth on the
 *     happy path.
 *   - No bundled JSON growth, no new CMS keys, no new persistence store,
 *     no AppHeader placement change, no sidebar/layout change, no
 *     i18n/config.ts change.
 *
 * Persistence (unchanged):
 *   - Calling `i18n.changeLanguage(next)` triggers the existing
 *     `languageChanged` handler in `i18n/config.ts`, which writes
 *     `localStorage.setItem('amuster_locale', next)` and updates document
 *     direction.
 *
 * Accessibility:
 *   - Keyboard navigable (Radix DropdownMenu).
 *   - Trigger aria-label is either the resolved CMS value or a short-code
 *     fallback ("EN" / "AR"). Never a raw `shell.language.*` key.
 *   - Active language marked by the radio-item indicator.
 *
 * Forbidden by this lane / repair:
 *   - No backend/API change, no useMutation, no TRP references, no Platform
 *     Admin shell edits, no AppHeader placement change, no sidebar/layout
 *     touch, no en.json/ar.json edits, no R__ change, no i18n/config.ts
 *     change.
 */

import { useCallback } from 'react';
import { HiLanguage } from 'react-icons/hi2';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useTranslationWithCMS } from '@/i18n/useTranslationWithCMS';

const RAW_KEY_PREFIX = 'shell.language.';

/**
 * Resolve a `useTranslationWithCMS` return value to a clean display string,
 * or null if the value is "unresolved" — defined as any of:
 *   - null / undefined,
 *   - empty string,
 *   - the i18n key itself (i18next's default fallback when no entry exists
 *     on either the CMS overrides or the bundled JSON),
 *   - any string starting with the `shell.language.` namespace prefix (catches
 *     adjacent raw keys if a translation chain were to substitute one key
 *     for another).
 *
 * Consumers MUST treat a null return as "do not show CMS text" and decide
 * locally between suppression (dropdown heading) and locale-native / short-
 * code fallback (aria-labels). This component never lets a raw
 * `shell.language.*` key reach the rendered DOM.
 */
export function resolveCmsValue(
  rawValue: unknown,
  key: string,
): string | null {
  if (rawValue === null || rawValue === undefined) return null;
  const s = typeof rawValue === 'string' ? rawValue : String(rawValue);
  if (s === '' || s === key) return null;
  if (s.startsWith(RAW_KEY_PREFIX)) return null;
  return s;
}

/**
 * Supported locale tuple. Mirrors `SUPPORTED_LOCALES` in
 * `apps/web/src/i18n/config.ts`; intentionally duplicated locally as a
 * literal-tuple type so the dropdown items render with stable order without
 * importing the config (which would couple this component to init-time
 * side effects).
 */
const LANGUAGE_OPTIONS: ReadonlyArray<{
  /** i18next locale code, used by `i18n.changeLanguage(code)`. */
  code: 'en' | 'ar';
  /** Locale-native option label. Intentionally not translated. */
  nativeLabel: string;
  /** i18n key for the announce-only label (screen reader). */
  announceKey: string;
}> = [
  {
    code: 'en',
    nativeLabel: 'English',
    announceKey: 'shell.language.option.en',
  },
  {
    code: 'ar',
    nativeLabel: 'العربية',
    announceKey: 'shell.language.option.ar',
  },
];

/**
 * Normalize an i18next language string (e.g. "en-US", "ar") to our two
 * supported short codes. Unknown locales fall back to 'en'.
 */
function normalizeLocale(lng: string | undefined): 'en' | 'ar' {
  if (!lng) return 'en';
  const lower = lng.toLowerCase();
  if (lower === 'ar' || lower.startsWith('ar-')) return 'ar';
  return 'en';
}

export function LanguageSelector() {
  const { t: rawT, i18n } = useTranslationWithCMS();

  const current = normalizeLocale(i18n?.language);

  const handleChange = useCallback(
    (next: string) => {
      if (next !== 'en' && next !== 'ar') return;
      if (next === current) return;
      // i18n.changeLanguage triggers the `languageChanged` handler wired in
      // i18n/config.ts → applyLocaleDirection() + localStorage write.
      void i18n.changeLanguage(next);
    },
    [i18n, current],
  );

  // Native option label for the current locale, used to show the active
  // language compactly next to the icon on desktop widths.
  const currentNativeLabel =
    LANGUAGE_OPTIONS.find((o) => o.code === current)?.nativeLabel ?? 'English';

  // ── Translation resolution (raw-key-fallback repair) ─────────────────────
  // Trigger aria-label: CMS value when available; otherwise the current
  // locale's short code uppercase ("EN" / "AR"). Never a raw key.
  const triggerAriaLabelKey = 'shell.language.selector.label';
  const triggerAriaLabel =
    resolveCmsValue(rawT(triggerAriaLabelKey), triggerAriaLabelKey) ??
    current.toUpperCase();

  // Dropdown heading: render only when the CMS value is resolved.
  // Suppress entirely otherwise (no hardcoded product-copy fallback).
  const menuHeadingKey = 'shell.language.selector.current';
  const menuHeading = resolveCmsValue(rawT(menuHeadingKey), menuHeadingKey);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-selector-trigger"
        aria-label={triggerAriaLabel}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <HiLanguage className="h-5 w-5" aria-hidden="true" />
        <span
          className="hidden text-xs font-medium uppercase tracking-wide sm:inline"
          data-testid="language-selector-current"
        >
          {current}
        </span>
        <span className="sr-only" data-testid="language-selector-current-sr">
          {currentNativeLabel}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {menuHeading !== null && (
          <>
            <DropdownMenuLabel data-testid="language-selector-heading">
              {menuHeading}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuRadioGroup value={current} onValueChange={handleChange}>
          {LANGUAGE_OPTIONS.map((opt) => {
            const optAriaLabel =
              resolveCmsValue(rawT(opt.announceKey), opt.announceKey) ??
              opt.code.toUpperCase();
            return (
              <DropdownMenuRadioItem
                key={opt.code}
                value={opt.code}
                data-testid={`language-selector-option-${opt.code}`}
                aria-label={optAriaLabel}
              >
                <span className="font-medium">{opt.nativeLabel}</span>
                <span className="ml-auto text-xs text-gray-500">
                  {opt.code.toUpperCase()}
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
